import React, { useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const ROLE_STYLE = {
  MERCHANT:           "bg-emerald-100 text-emerald-700",
  DISTRIBUTOR:        "bg-blue-100 text-blue-700",
  MASTER_DISTRIBUTOR: "bg-violet-100 text-violet-700",
};

const DeleteRateModal = ({ serviceType, target, onClose }) => {
  const [userId,  setUserId]  = useState(target?.userId   || "");
  const userType              =           target?.userType || "MERCHANT";
  const [step,    setStep]    = useState(target?.userId && target?.userType ? "confirm" : "form");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const { callApi } = useApi(
    `/commission/rates/service/${serviceType}/user-type/${userType}/user/${userId}`,
    "DELETE"
  );

  const handleDelete = async () => {
    try {
      setLoading(true); setError("");
      await callApi();
      setStep("done");
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.message || "Delete failed. Please try again.");
      setStep("confirm");
    } finally {
      setLoading(false);
    }
  };

  if (step === "done") return (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center text-3xl">✓</div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Rates Deleted</h2>
      <p className="text-gray-400 text-sm">
        All rates for <span className="font-semibold text-gray-700">{userId}</span> ({userType}) under {serviceType} removed.
      </p>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600 text-xl font-bold">!</div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Delete User Rates</h2>
          <p className="text-xs text-gray-400">Service: <span className="font-semibold text-gray-600">{serviceType}</span></p>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {step === "form" && (
        <div className="space-y-4">
          {/* User ID */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">User ID</label>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter username"
              className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-red-400 focus:bg-white transition"
            />
          </div>

          {/* User Type — read-only */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">User Type</label>
            <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${ROLE_STYLE[userType] || "bg-gray-100 text-gray-500"}`}>
              {userType.replace(/_/g, " ")}
            </div>
          </div>

          <button
            onClick={() => {
              if (!userId.trim()) { setError("Please enter a User ID"); return; }
              setError(""); setStep("confirm");
            }}
            className="w-full py-3.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition"
          >
            Continue
          </button>
        </div>
      )}

      {step === "confirm" && (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
            <p className="text-sm font-bold text-red-700 mb-3">This will permanently delete all rates for:</p>
            <div className="space-y-2.5">
              {[["User ID", userId], ["User Type", userType], ["Service", serviceType]].map(([l, v]) => (
                <div key={l} className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 font-medium">{l}</span>
                  <span className={`text-sm font-bold ${l === "User Type" ? (ROLE_STYLE[v] ? "px-2.5 py-0.5 rounded-full text-xs " + ROLE_STYLE[v] : "text-gray-800") : "text-gray-800"}`}>
                    {v.replace(/_/g, " ")}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-red-200">
              <p className="text-xs text-red-500 font-semibold text-center">⚠ This action cannot be undone</p>
            </div>
          </div>

          <div className="flex gap-3">
            {!target?.userId && (
              <button onClick={() => setStep("form")}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition">
                Go Back
              </button>
            )}
            <button onClick={handleDelete} disabled={loading}
              className={`flex-1 py-3 rounded-xl text-white font-bold text-sm transition ${loading ? "bg-gray-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}>
              {loading ? "Deleting..." : "Yes, Delete All Rates"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteRateModal;
